import { useEffect, useState } from "react";
import { FileText, X, AlertTriangle, FileWarning, Shield, ShieldOff, Trash2, Plus, Check } from "lucide-react";
import AdminShell, { Panel, StatusPill, Btn } from "@/components/admin/AdminShell";
import KpiCard from "@/components/admin/KpiCard";
import { PrimaryTabs, FilterChips } from "@/components/admin/SubTabs";
import DataTable from "@/components/admin/DataTable";
import AdminPager from "@/components/admin/AdminPager";
import { api } from "@/lib/api";
import { asAdminList, useAdminPager, unwrapAdminList } from "@/lib/useAdminPager";

const TABS = [
  { id: "monitoring", label: "Transaction Monitoring", icon: AlertTriangle },
  { id: "velocity_rules", label: "Velocity Rules", icon: Shield },
  { id: "reporting", label: "FIU Reporting", icon: FileWarning },
  { id: "blacklist", label: "Blacklists & Sanctions", icon: ShieldOff },
];

const PATTERN_FILTERS = [{ id: "", label: "All" }, { id: "Structuring", label: "Structuring" }, { id: "Layering", label: "Layering" }, { id: "Rapid Movement", label: "Rapid Movement" }, { id: "High Risk Jurisdiction", label: "High Risk Jurisdiction" }, { id: "Velocity Alert", label: "Velocity" }];
const fmt = (v) => v?.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }) || "—";

export default function FiuAml() {
  const [tab, setTab] = useState("monitoring");
  const [alerts, setAlerts] = useState([]);
  const [velocity, setVelocity] = useState([]);
  const [strs, setStrs] = useState([]);
  const [velocityRules, setVelocityRules] = useState([]);
  const [filing, setFiling] = useState(null);
  const [patternFilter, setPatternFilter] = useState("");
  const alertsPager = useAdminPager();
  const velocityPager = useAdminPager();
  const strPager = useAdminPager();
  
  // Rule Editor State
  const [editingRule, setEditingRule] = useState(null);
  const [isCreatingRule, setIsCreatingRule] = useState(false);
  const [ruleForm, setRuleForm] = useState({ rule_id: "", name: "", metric: "", threshold: 0, time_window: 60, action: "alert_only", enabled: true });

  async function load() {
    const [a, v, s, rules] = await Promise.all([
      api.get("/api/admin/aml/alerts", { params: { skip: alertsPager.skip, limit: alertsPager.limit } }),
      api.get("/api/admin/aml/velocity-alerts", { params: { skip: velocityPager.skip, limit: velocityPager.limit } }),
      api.get("/api/admin/aml/str-reports", { params: { skip: strPager.skip, limit: strPager.limit } }),
      api.get("/api/admin/safety/velocity-rules"),
    ]);
    const alerts = unwrapAdminList(a.data, "alerts");
    setAlerts(alerts.items);
    alertsPager.setTotal(alerts.total);
    const vel = unwrapAdminList(v.data);
    setVelocity(vel.items);
    velocityPager.setTotal(vel.total);
    const strs = unwrapAdminList(s.data);
    setStrs(strs.items);
    strPager.setTotal(strs.total);
    setVelocityRules(rules.data || []);
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [alertsPager.page, velocityPager.page, strPager.page]);

  async function clearAlert(id) {
    await api.post(`/api/admin/aml/alerts/${id}/clear`);
    load();
  }

  async function handleUnfreeze(userId) {
    try {
      await api.post("/api/admin/safety/unfreeze-user", { user_id: userId });
      alert("User unverified and trading unfrozen successfully.");
      load();
    } catch (e) {
      alert("Failed to unfreeze user: " + (e.response?.data?.detail || e.message));
    }
  }

  async function handleSaveRule(e) {
    e.preventDefault();
    try {
      await api.post("/api/admin/safety/velocity-rules", ruleForm);
      setIsCreatingRule(false);
      setEditingRule(null);
      load();
    } catch (e) {
      alert("Failed to save rule: " + (e.response?.data?.detail || e.message));
    }
  }

  async function handleDeleteRule(ruleId) {
    if (!confirm("Are you sure you want to delete this rule?")) return;
    try {
      await api.delete(`/api/admin/safety/velocity-rules/${ruleId}`);
      load();
    } catch (e) {
      alert("Failed to delete rule");
    }
  }

  const alertRows = asAdminList(alerts);
  const pending = alertRows.filter((a) => a.status === "pending review").length;
  const escalated = alertRows.filter((a) => a.status === "escalated").length;
  const filteredAlerts = alertRows.filter((a) => !patternFilter || a.type === patternFilter);

  return (
    <AdminShell title="FIU / AML">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <KpiCard label="Pending Reviews" value={pending} icon={AlertTriangle} variant="yellow" />
        <KpiCard label="Escalated" value={escalated} icon={Shield} variant="purple" />
        <KpiCard label="Filed STRs (MTD)" value={strs.length} icon={FileText} variant="red" />
        <KpiCard label="Velocity Alerts" value={velocity.length} delta={`${velocity.filter(v => v.risk_level === "critical").length} critical`} icon={AlertTriangle} variant="red" />
      </div>

      <PrimaryTabs tabs={TABS} value={tab} onChange={setTab} testIdPrefix="aml-tab" />
      
      <div className="mt-5">
        {tab === "monitoring" && (
          <div className="space-y-5">
            <Panel>
              <div className="p-4 border-b border-[#1E2845] flex justify-between items-center">
                <h3 className="text-[14px] font-bold">Suspicious Pattern Detection</h3>
                <FilterChips options={PATTERN_FILTERS} value={patternFilter} onChange={setPatternFilter} testIdPrefix="pattern" />
              </div>
              <DataTable
                testid="aml-alerts-table"
                columns={[
                  { key: "user", header: "User", render: (a) => <div><div className="font-bold">{a.user_name}</div><div className="text-[11px] text-[#8892A8]">{a.user_email}</div></div> },
                  { key: "type", header: "Pattern", render: (a) => <div><div className="font-semibold">{a.type}</div><div className="text-[11px] text-[#8892A8] max-w-[280px] truncate">{a.pattern}</div></div> },
                  { key: "amount", header: "Amount", align: "right", render: (a) => <span className="tabular-nums">{fmt(a.amount_inr)}</span> },
                  { key: "score", header: "Risk", align: "right", render: (a) => <span className={`font-bold ${a.risk_score >= 90 ? "text-[#EF4444]" : a.risk_score >= 70 ? "text-[#F59E0B]" : "text-[#10B981]"}`}>{a.risk_score}</span> },
                  { key: "status", header: "Status", render: (a) => <StatusPill value={a.status} /> },
                  { key: "actions", header: "Actions", align: "right", render: (a) => (
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setFiling(a)} className="text-[#EF4444] text-[11px] hover:underline" data-testid={`file-str-${a.alert_id}`}>File STR</button>
                      <button onClick={() => clearAlert(a.id)} className="text-[#8892A8] text-[11px] hover:underline">Clear</button>
                    </div>
                  )},
                ]}
                rows={filteredAlerts}
                rowKey={(a) => a.id}
              />
              <AdminPager page={alertsPager.page} total={alertsPager.total} pageSize={alertsPager.pageSize} onPage={alertsPager.setPage} testId="aml-alerts-pager" />
            </Panel>

            <Panel>
              <div className="p-4 border-b border-[#1E2845]"><h3 className="text-[14px] font-bold">Velocity Check Alerts</h3></div>
              <DataTable
                columns={[
                  { key: "user", header: "User", render: (v) => <div><div className="font-bold">{v.user_name}</div><div className="text-[11px] text-[#8892A8]">{v.user_email}</div></div> },
                  { key: "metric", header: "Metric" },
                  { key: "current", header: "Current", align: "right", render: (v) => <span className="font-bold tabular-nums">{v.current.toLocaleString()}</span> },
                  { key: "threshold", header: "Threshold", align: "right", render: (v) => <span className="text-[#8892A8] tabular-nums">{v.threshold.toLocaleString()}</span> },
                  { key: "risk", header: "Risk", render: (v) => <StatusPill value={v.risk_level} /> },
                  { key: "status", header: "Status", render: (v) => <StatusPill value={v.status} /> },
                  { key: "freeze_status", header: "Compliance Action", align: "right", render: (v) => (
                    v.risk_level === "critical" ? (
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-[10px] text-[#EF4444] bg-[#EF4444]/10 px-2 py-0.5 rounded-[4px] font-semibold border border-[#EF4444]/20">AUTO-FROZEN</span>
                        <button onClick={() => handleUnfreeze(v.user_id)} className="text-[#3B82F6] text-[11px] hover:underline font-semibold">Unfreeze Account</button>
                      </div>
                    ) : (
                      <span className="text-[11px] text-[#8892A8]">Monitor Only</span>
                    )
                  )}
                ]}
                rows={velocity}
                rowKey={(v) => v.alert_id}
              />
              <AdminPager page={velocityPager.page} total={velocityPager.total} pageSize={velocityPager.pageSize} onPage={velocityPager.setPage} testId="aml-velocity-pager" />
            </Panel>
          </div>
        )}

        {tab === "velocity_rules" && (
          <div className="space-y-5">
            <div className="flex justify-between items-center mb-1">
              <h2 className="text-[15px] font-bold">Configure Transaction Velocity Limits</h2>
              <Btn onClick={() => {
                setRuleForm({ rule_id: "", name: "", metric: "", threshold: 0, time_window: 60, action: "alert_only", enabled: true });
                setIsCreatingRule(true);
              }} className="h-9"><Plus size={14} /> Create New Rule</Btn>
            </div>
            
            <Panel>
              <DataTable
                columns={[
                  { key: "name", header: "Rule Name", render: (r) => <span className="font-bold">{r.name}</span> },
                  { key: "metric", header: "Metric" },
                  { key: "threshold", header: "Limit Threshold", align: "right", render: (r) => <span className="font-mono">{r.threshold.toLocaleString()}</span> },
                  { key: "window", header: "Time Window", align: "right", render: (r) => <span className="text-[#8892A8]">{r.time_window}s</span> },
                  { key: "action", header: "Trigger Action", render: (r) => <span className={`text-[12px] font-semibold ${r.action === "freeze_trading" ? "text-[#EF4444]" : "text-[#F59E0B]"}`}>{r.action === "freeze_trading" ? "Freeze Account" : "Alert Admin"}</span> },
                  { key: "enabled", header: "Status", render: (r) => <StatusPill value={r.enabled ? "active" : "disabled"} /> },
                  { key: "actions", header: "Actions", align: "right", render: (r) => (
                    <div className="flex justify-end gap-3">
                      <button onClick={() => {
                        setRuleForm(r);
                        setIsCreatingRule(true);
                      }} className="text-[#3B82F6] text-[12px] hover:underline">Edit</button>
                      <button onClick={() => handleDeleteRule(r.rule_id)} className="text-[#EF4444] text-[12px] hover:underline"><Trash2 size={13} /></button>
                    </div>
                  )}
                ]}
                rows={velocityRules}
                rowKey={(r) => r.rule_id}
              />
            </Panel>
          </div>
        )}

        {tab === "reporting" && (
          <Panel>
            <div className="p-4 border-b border-[#1E2845]"><h3 className="text-[14px] font-bold">STR Reports Filed with FIU-IND</h3></div>
            <DataTable
              columns={[
                { key: "filed_at", header: "Filed At", render: (s) => <span className="text-[#8892A8]">{new Date(s.filed_at).toLocaleString()}</span> },
                { key: "user_id", header: "User ID" },
                { key: "pattern", header: "Pattern" },
                { key: "amount", header: "Amount", align: "right", render: (s) => fmt(s.amount_inr) },
                { key: "filed_by", header: "Filed By" },
                { key: "status", header: "Status", render: (s) => <StatusPill value={s.status} /> },
              ]}
              rows={strs}
              rowKey={(s) => s.id}
              empty="No STRs filed yet."
            />
            <AdminPager page={strPager.page} total={strPager.total} pageSize={strPager.pageSize} onPage={strPager.setPage} testId="aml-str-pager" />
          </Panel>
        )}

        {tab === "blacklist" && (
          <Panel className="p-10 text-center text-[#8892A8] text-[13px]">Sanctions / blacklist screening — wires in Phase H with Refinitiv / ComplyAdvantage / OFAC list ingestion.</Panel>
        )}
      </div>

      {/* Rule Creator Modal */}
      {isCreatingRule && (
        <div className="fixed inset-0 z-[100] bg-black/60 grid place-items-center p-4" onClick={() => setIsCreatingRule(false)}>
          <form onSubmit={handleSaveRule} onClick={(e) => e.stopPropagation()} className="bg-[#0F1626] border border-[#1E2845] rounded-[14px] w-full max-w-[500px] p-5">
            <div className="flex justify-between mb-4">
              <h3 className="text-[16px] font-bold">{ruleForm.rule_id ? "Edit Velocity Rule" : "Create Velocity Rule"}</h3>
              <button type="button" onClick={() => setIsCreatingRule(false)} className="text-[#8892A8]"><X size={16} /></button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-[12px] text-[#8892A8] mb-1">Rule ID</label>
                <input 
                  type="text" 
                  disabled={!!ruleForm.rule_id}
                  value={ruleForm.rule_id} 
                  onChange={(e) => setRuleForm({ ...ruleForm, rule_id: e.target.value })} 
                  placeholder="e.g. max_deposits_1h"
                  required
                  className="w-full p-2 bg-[#0A0F1F] border border-[#1E2845] rounded-[8px] text-[13px] disabled:opacity-50"
                />
              </div>

              <div>
                <label className="block text-[12px] text-[#8892A8] mb-1">Rule Name</label>
                <input 
                  type="text" 
                  value={ruleForm.name} 
                  onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })} 
                  placeholder="e.g. Hourly Deposit Max"
                  required
                  className="w-full p-2 bg-[#0A0F1F] border border-[#1E2845] rounded-[8px] text-[13px]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[12px] text-[#8892A8] mb-1">Metric Description</label>
                  <input 
                    type="text" 
                    value={ruleForm.metric} 
                    onChange={(e) => setRuleForm({ ...ruleForm, metric: e.target.value })} 
                    placeholder="e.g. Deposit/hour"
                    required
                    className="w-full p-2 bg-[#0A0F1F] border border-[#1E2845] rounded-[8px] text-[13px]"
                  />
                </div>
                <div>
                  <label className="block text-[12px] text-[#8892A8] mb-1">Limit Threshold (Value)</label>
                  <input 
                    type="number" 
                    value={ruleForm.threshold} 
                    onChange={(e) => setRuleForm({ ...ruleForm, threshold: parseFloat(e.target.value) })} 
                    required
                    className="w-full p-2 bg-[#0A0F1F] border border-[#1E2845] rounded-[8px] text-[13px]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[12px] text-[#8892A8] mb-1">Time Window (seconds)</label>
                  <input 
                    type="number" 
                    value={ruleForm.time_window} 
                    onChange={(e) => setRuleForm({ ...ruleForm, time_window: parseInt(e.target.value) })} 
                    required
                    className="w-full p-2 bg-[#0A0F1F] border border-[#1E2845] rounded-[8px] text-[13px]"
                  />
                </div>
                <div>
                  <label className="block text-[12px] text-[#8892A8] mb-1">Action on Breach</label>
                  <select 
                    value={ruleForm.action} 
                    onChange={(e) => setRuleForm({ ...ruleForm, action: e.target.value })}
                    className="w-full p-2 bg-[#0A0F1F] border border-[#1E2845] rounded-[8px] text-[13px]"
                  >
                    <option value="alert_only">Alert Compliance Officer</option>
                    <option value="freeze_trading">Freeze User Trading</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="enabled"
                  checked={ruleForm.enabled} 
                  onChange={(e) => setRuleForm({ ...ruleForm, enabled: e.target.checked })} 
                />
                <label htmlFor="enabled" className="text-[13px] text-[#E5E9F2]">Rule Enabled</label>
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <Btn type="button" kind="ghost" onClick={() => setIsCreatingRule(false)} className="flex-1">Cancel</Btn>
              <Btn type="submit" kind="primary" className="flex-1"><Check size={14} /> Save Rule</Btn>
            </div>
          </form>
        </div>
      )}

      {filing && <StrModal alert={filing} onClose={() => setFiling(null)} onFiled={() => { setFiling(null); load(); }} />}
    </AdminShell>
  );
}

function StrModal({ alert, onClose, onFiled }) {
  const [notes, setNotes] = useState("");
  async function file() {
    await api.post("/api/admin/aml/file-str", { user_id: alert.user_id, pattern: alert.type, amount_inr: alert.amount_inr, notes });
    onFiled();
  }
  return (
    <div className="fixed inset-0 z-[100] bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0F1626] border border-[#1E2845] rounded-[14px] w-full max-w-[500px] p-5" data-testid="str-modal">
        <div className="flex justify-between mb-4"><h3 className="text-[16px] font-bold">File Suspicious Transaction Report</h3><button onClick={onClose} className="text-[#8892A8]"><X size={16} /></button></div>
        <div className="text-[13px] text-[#8892A8] mb-3">User: <span className="text-[#E5E9F2]">{alert.user_email}</span></div>
        <div className="text-[13px] text-[#8892A8] mb-3">Pattern: <span className="text-[#E5E9F2]">{alert.type}</span> · Risk score {alert.risk_score}</div>
        <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Compliance notes for FIU..." className="w-full p-3 bg-[#0A0F1F] border border-[#1E2845] rounded-[8px] text-[13px]" data-testid="str-notes" />
        <Btn kind="danger" onClick={file} className="w-full mt-4" data-testid="str-submit"><FileText size={14} /> File STR with FIU-IND</Btn>
      </div>
    </div>
  );
}
